import type { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchMediaBytes } from '@/lib/contentStudio';

// ─────────────────────────────────────────────────────────────
// ASSEMBLAGE FINAL (FFmpeg.wasm, dans le navigateur)
// Concatène les vidéos de chaque scène en un seul MP4, en unifiant la
// résolution et l'image par seconde, et incruste (optionnel) les sous-titres.
// Aucun serveur requis : tout se passe dans l'onglet de l'utilisateur.
// ─────────────────────────────────────────────────────────────

let instance: FFmpeg | null = null;
let loading: Promise<FFmpeg> | null = null;

async function loadFFmpeg(): Promise<FFmpeg> {
  if (instance) return instance;
  if (loading) return loading;
  loading = (async () => {
    const [{ FFmpeg: FFmpegClass }, { toBlobURL }] = await Promise.all([
      import('@ffmpeg/ffmpeg'),
      import('@ffmpeg/util'),
    ]);
    const coreBase = 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd';
    const ff = new FFmpegClass();
    await ff.load({
      coreURL: await toBlobURL(`${coreBase}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${coreBase}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    instance = ff;
    return ff;
  })();
  return loading;
}

export interface AssembleOptions {
  urls: string[];
  width?: number;
  height?: number;
  fps?: number;
  srt?: string | null;
  onProgress?: (ratio: number) => void;
}

export interface AssembleResult {
  blob: Blob;
  subtitlesBurned: boolean;
}

function toUint8(data: Uint8Array | string): Uint8Array {
  return data instanceof Uint8Array ? data : new TextEncoder().encode(String(data));
}

/**
 * Assemble une liste de vidéos (dans l'ordre) en un seul MP4.
 * Chaque vidéo est normalisée (mise à l'échelle + centrage) avant concaténation.
 */
export async function assembleScenes(options: AssembleOptions): Promise<AssembleResult> {
  const { urls, width = 1280, height = 720, fps = 24, srt, onProgress } = options;
  if (urls.length === 0) throw new Error('Aucune vidéo à assembler.');

  const ff = await loadFFmpeg();
  const progressHandler = ({ progress }: { progress: number }) => {
    if (onProgress) onProgress(Math.max(0, Math.min(1, Number(progress) || 0)));
  };
  ff.on('progress', progressHandler);

  try {
    // 1) Écriture de chaque clip dans le système de fichiers virtuel.
    for (let i = 0; i < urls.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const bytes = await fetchMediaBytes(urls[i]);
      // eslint-disable-next-line no-await-in-loop
      await ff.writeFile(`in${i}.mp4`, bytes);
    }
    if (srt) await ff.writeFile('subs.srt', new TextEncoder().encode(srt));

    const inputs: string[] = [];
    urls.forEach((_, i) => { inputs.push('-i', `in${i}.mp4`); });

    const scaleParts = urls.map((_, i) =>
      `[${i}:v]scale=${width}:${height}:force_original_aspect_ratio=decrease,pad=${width}:${height}:(ow-iw)/2:(oh-ih)/2,setsar=1,fps=${fps}[v${i}]`);
    const concatInputs = urls.map((_, i) => `[v${i}]`).join('');
    const baseFilter = `${scaleParts.join(';')};${concatInputs}concat=n=${urls.length}:v=1:a=0[vout]`;
    const srtFilter = `${baseFilter};[vout]subtitles=subs.srt[vfinal]`;

    const encode = ['-c:v', 'libx264', '-preset', 'veryfast', '-crf', '24', '-pix_fmt', 'yuv420p', '-movflags', '+faststart', 'out.mp4'];
    const baseArgs = [...inputs, '-filter_complex', baseFilter, '-map', '[vout]', ...encode];
    const srtArgs = [...inputs, '-filter_complex', srtFilter, '-map', '[vfinal]', ...encode];

    let subtitlesBurned = false;
    if (srt) {
      try {
        await ff.exec(srtArgs);
        subtitlesBurned = true;
      } catch {
        // Certaines builds FFmpeg n'ont pas le filtre subtitles : on réassemble sans.
        try { await ff.deleteFile('out.mp4'); } catch { /* ignore */ }
        await ff.exec(baseArgs);
      }
    } else {
      await ff.exec(baseArgs);
    }

    const data = await ff.readFile('out.mp4');
    const bytes = toUint8(data);
    const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    return { blob: new Blob([buffer], { type: 'video/mp4' }), subtitlesBurned };
  } finally {
    ff.off('progress', progressHandler);
    for (let i = 0; i < urls.length; i += 1) {
      try { await ff.deleteFile(`in${i}.mp4`); } catch { /* ignore */ }
    }
    if (srt) { try { await ff.deleteFile('subs.srt'); } catch { /* ignore */ } }
    try { await ff.deleteFile('out.mp4'); } catch { /* ignore */ }
  }
}